import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  ReportExpense,
  useRemoveExpenseFromReportMutation,
} from "@common/utils/graphql";
import { useState } from "react";
import { EditReimbursementSheetContent } from "./EditReimbursementSheetContent";
import { FilePreview } from "@/components/ui/file-preview";
import { SheetBackButton } from "@common/components/SheetActions/SheetBackButton";
import { formatDollars, formatDate } from "@common/components/utils";
import { Trash } from "@phosphor-icons/react";
import { Body } from "@hypercard/web-components";
import { useUpdateExpenseOutOfPolicyState } from "@common/state/outOfPolicyState";
import { ExpenseStatusText } from "../employee/ExpenseStatusText";
import { format } from "date-fns";
import { StatusText } from "@common/components/StatusText";
import { ExpenseCustomFieldValues } from "@common/components/CustomFields";

interface ViewOrEditReportExpenseProps {
  expense: ReportExpense;
  onBack: () => void;
  allowEdit?: boolean;
  totalExpenses: number;
}

export const ViewOrEditReportExpense = ({
  expense,
  onBack,
  allowEdit,
  totalExpenses,
}: ViewOrEditReportExpenseProps) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [removeExpenseFromReport, { loading: isDeleting }] =
    useRemoveExpenseFromReportMutation();
  const updateExpenseOutOfPolicyState = useUpdateExpenseOutOfPolicyState();

  const handleDelete = async () => {
    await removeExpenseFromReport({
      variables: {
        reportId: expense.reportId,
        expenseId: expense.id,
      },
    });
    updateExpenseOutOfPolicyState(expense.id);
    onBack();
  };

  if (isEditMode) {
    return (
      <EditReimbursementSheetContent
        expense={expense}
        onBack={() => {
          setIsEditMode(false);
          onBack();
        }}
      />
    );
  }

  const actions = (
    <>
      {allowEdit && (
        <div className="flex w-full flex-col gap-4 p-sheet-gap">
          <Button
            variant="secondary"
            size="sm"
            disabled={isDeleting}
            onClick={() => setIsEditMode(true)}
          >
            Edit Expense
          </Button>
          <Button
            disabled={isDeleting}
            size="sm"
            variant="destructive"
            onClick={handleDelete}
          >
            <Trash className="mr-2 size-4" />
            {isDeleting ? "Deleting Expense" : "Delete Expense"}
          </Button>
        </div>
      )}
    </>
  );

  const header = (
    <SheetHeader className="flex flex-col gap-y-4 border-b bg-transparent pb-6">
      <div className="flex h-5 items-center gap-x-3">
        <time
          className="text-body-sm text-white/70"
          dateTime={format(expense.createdAt, "yyyy-MM-dd")}
        >
          {formatDate(expense.createdAt)}
        </time>
        <Separator orientation="vertical" className="h-3 bg-white/30" />
        <ExpenseStatusText expense={expense} small />
      </div>
      <SheetTitle>{expense.merchantName}</SheetTitle>
      <Body size="lg" weight="regular" className="flex items-center gap-x-3">
        <span>{expense.expenseCategory?.categoryName || "No Category"}</span>
        <Separator orientation="vertical" className="h-6 bg-white/30" />
        <span>{formatDollars(expense.amount)}</span>
      </Body>
    </SheetHeader>
  );
  const { approvedOrRejectedBy } = expense.report;
  return (
    <>
      <SheetBackButton onClick={onBack} disabled={isDeleting} />
      {header}
      <div className="grow overflow-auto scrollbar-thin">
        <div className="flex flex-col gap-3 bg-transparent px-sheet-gap">
          <ExpenseCustomFieldValues
            customFieldEntries={expense.customFieldEntries}
          />
          <div>
            <Label>Notes</Label>
            <Body size="md" weight="regular">
              {expense.notes || "No notes"}
            </Body>
          </div>
          <div>
            <Label>Receipt</Label>
            <FilePreview url={expense.receiptFileUrl} />
          </div>
          <div>
            <Label>Attendees</Label>
            <Body size="md" weight="regular">
              {expense.attendeeEmails.length === 0
                ? "No attendees"
                : expense.attendeeEmails.map((email) => (
                    <div key={`attendee-${email}`}>{email}</div>
                  ))}
            </Body>
          </div>
          <div>
            <Label>Added to Report</Label>
            <div className="flex flex-row gap-3">
              <div className="mt-2 flex w-full flex-col rounded-md bg-[#1B1B1B] p-4">
                <div className="mb-4 flex h-5">
                  <Body size="sm" weight="regular" className="text-white/70">
                    {formatDate(expense.report.updatedAt)}
                  </Body>
                  <Separator
                    orientation="vertical"
                    className="mx-3 h-4 bg-white/30"
                  />
                  <StatusText {...expense.report} size="sm" />
                </div>
                <Body size="lg" weight="regular">
                  {expense.report.title}
                </Body>
                <div className="mt-2 flex h-5">
                  <Body size="md" weight="regular" className="text-slate">
                    {`${totalExpenses} Expense${totalExpenses !== 1 ? "s" : ""}`}
                  </Body>
                  <Separator
                    orientation="vertical"
                    className="mx-3 bg-white/30"
                  />
                  <Body size="md" weight="regular" className="text-slate">
                    {expense.report.displayTotalDollarAmount}
                  </Body>
                </div>
              </div>
            </div>
          </div>
          {approvedOrRejectedBy?.displayName && (
            <div>
              <Label>Report Reviewed By</Label>
              <Body size="md" weight="regular">
                {approvedOrRejectedBy?.displayName}
              </Body>
            </div>
          )}
        </div>
        {actions}
      </div>
    </>
  );
};
